import React from 'react';
import { Box, Typography, Select, MenuItem, TextField, Button } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import { AppointmentTypes } from '../../../types/appointment.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

/* ─── Constants matching the dashboard FilterBar style ────────────── */

const COLORS = {
    primary: '#206FA5',
    grayMedium: '#8A94A6',
    grayDark: '#6B7A90',
    textPrimary: '#1a1a2e',
    border: '#E8ECF0',
};

const labelSx = {
    fontSize: '10px',
    color: COLORS.grayMedium,
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
};

const selectSx = {
    minWidth: '150px',
    fontSize: '13px',
    fontFamily: 'Poppins, sans-serif',
    borderRadius: '6px',
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.border,
    },
    '& .MuiSelect-select': {
        padding: '6px 28px 6px 10px',
    },
};

const textFieldSx = {
    minWidth: '180px',
    '& .MuiInputBase-root': {
        fontSize: '13px',
        fontFamily: 'Poppins, sans-serif',
        borderRadius: '6px',
        height: '36px',
    },
    '& .MuiInputBase-input': {
        padding: '6px 10px',
        fontSize: '13px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.border,
    },
};

const menuItemSx = {
    fontSize: '13px',
    fontFamily: 'Poppins, sans-serif',
};

const TIME_PERIOD_OPTIONS = [
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' },
];

const dateFieldSx = {
    width: '150px',
    '& .MuiInputBase-root': {
        fontSize: '13px',
        fontFamily: 'Poppins, sans-serif',
        borderRadius: '6px',
        height: '36px',
    },
    '& .MuiInputBase-input': {
        padding: '6px 10px',
        fontSize: '13px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.border,
    },
};

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'escalated', label: 'Escalated' },
    { value: 'no-checkin', label: 'No Check-In' },
    { value: 'cancelled', label: 'Cancelled' },
];

/* ─── Props ───────────────────────────────────────────────────────── */

interface Props {
    onFilter(filters: AppointmentTypes.Filters): Promise<void>;
    onDownload(): Promise<void>;
    activeTab?: 'active' | 'past';
}

/* ─── Component ───────────────────────────────────────────────────── */

export const AppointmentsListFilters: React.FC<Props> = ({
    onFilter,
    onDownload,
    activeTab = 'active',
}) => {
    const { loading } = useSelector((state: RootState) => state.appointment);

    // Local filter state (applied on "Apply" click)
    const [userName, setUserName] = React.useState('');
    const [title, setTitle] = React.useState('');
    const [status, setStatus] = React.useState('all');
    const [timePeriod, setTimePeriod] = React.useState('30');
    const [customStartDate, setCustomStartDate] = React.useState('');
    const [customEndDate, setCustomEndDate] = React.useState('');

    const handleApply = async () => {
        const filters: AppointmentTypes.Filters = {} as AppointmentTypes.Filters;
        if (userName.trim()) (filters as any).userName = userName.trim();
        if (title.trim()) (filters as any).title = title.trim();
        if (activeTab === 'past') {
            if (status && status !== 'all') (filters as any).status = status;
            if (timePeriod === 'custom') {
                (filters as any).timePeriod = 'custom';
                if (customStartDate) (filters as any).startDate = customStartDate;
                if (customEndDate) (filters as any).endDate = customEndDate;
            } else if (timePeriod) {
                (filters as any).timePeriod = timePeriod;
            }
        }
        await onFilter(filters);
    };

    const handleClear = async () => {
        setUserName('');
        setTitle('');
        setStatus('all');
        setTimePeriod('30');
        setCustomStartDate('');
        setCustomEndDate('');
        await onFilter({} as AppointmentTypes.Filters);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleApply();
        }
    };

    return (
        <Box sx={{ padding: '16px 16px 8px 16px' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '10px',
                    flexWrap: 'wrap',
                }}
            >
                {/* User Name */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>User Name</Typography>
                    <TextField
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Filter by user name"
                        size="small"
                        sx={textFieldSx}
                    />
                </Box>

                {/* Title */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Title</Typography>
                    <TextField
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Filter by title"
                        size="small"
                        sx={textFieldSx}
                    />
                </Box>

                {/* Status (Past tab only) */}
                {activeTab === 'past' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Status</Typography>
                        <Select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            size="small"
                            sx={selectSx}
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={menuItemSx}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}

                {/* Time Period (Past tab only) */}
                {activeTab === 'past' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Time Period</Typography>
                        <Select
                            value={timePeriod}
                            onChange={(e) => setTimePeriod(e.target.value)}
                            size="small"
                            sx={selectSx}
                        >
                            {TIME_PERIOD_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={menuItemSx}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}

                {/* Custom date range (Past tab + Custom selected) */}
                {activeTab === 'past' && timePeriod === 'custom' && (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <Typography sx={labelSx}>Start Date</Typography>
                            <TextField
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                size="small"
                                sx={dateFieldSx}
                                inputProps={{
                                    max: customEndDate || dayjs().format('YYYY-MM-DD'),
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <Typography sx={labelSx}>End Date</Typography>
                            <TextField
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                size="small"
                                sx={dateFieldSx}
                                inputProps={{
                                    min: customStartDate || undefined,
                                    max: dayjs().format('YYYY-MM-DD'),
                                }}
                            />
                        </Box>
                    </>
                )}

                {/* Apply */}
                <Button
                    onClick={handleApply}
                    disabled={loading?.fetchEnriched}
                    sx={{
                        padding: '6px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        backgroundColor: COLORS.primary,
                        color: '#fff',
                        textTransform: 'none',
                        fontFamily: 'Poppins, sans-serif',
                        '&:hover': { backgroundColor: '#2b5a8a' },
                        '&.Mui-disabled': {
                            backgroundColor: COLORS.border,
                            color: COLORS.grayMedium,
                        },
                    }}
                >
                    Apply
                </Button>

                {/* Clear */}
                <Typography
                    onClick={loading?.fetchEnriched ? undefined : handleClear}
                    sx={{
                        padding: '6px 10px',
                        fontSize: '13px',
                        cursor: loading?.fetchEnriched ? 'default' : 'pointer',
                        color: loading?.fetchEnriched ? COLORS.grayMedium : COLORS.grayDark,
                        '&:hover': loading?.fetchEnriched
                            ? {}
                            : { color: COLORS.textPrimary },
                    }}
                >
                    Clear
                </Typography>

                {/* Spacer */}
                <Box sx={{ flex: 1 }} />

                {/* Export */}
                <Typography
                    onClick={onDownload}
                    sx={{
                        fontSize: '12px',
                        color: COLORS.primary,
                        cursor: 'pointer',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    <FileDownloadOutlinedIcon sx={{ fontSize: '14px' }} />
                    Export
                </Typography>
            </Box>
        </Box>
    );
};
